import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import { AdminPageHeader } from '../../Admin/Common/AdminPagesHeader';
import {
    DOC_CUSTOMER,
    DOC_CUSTOMER_COLUMNS,
    documentPriorityOptions,
    documentStatusOptions,
    QUERY_KEYS,
} from '../../../constants';
import { getAllDocumentRequestByAccount, popup } from '../../../utils';
import { usePagination } from '../../../hooks';
import { Table } from '../../shared';
import { useDebouncedValue } from '../../../hooks/useDebounceValue';

export const DocumentListing = () => {
    const location = useLocation();
    const [searchParams] = useSearchParams();

    const [response, setResponse] = useState({ data: [], count: 0 });
    const [filter, setFilter] = useState({ priority: '', status: '' });
    const [selectedId, setSelectedId] = useState({
        priorityId: '1',
        statusId: '1',
    });
    const [search, setSearch] = useState('');

    const accountId = useSelector((state) => state.accountArray.selectedAccount);
    const pagination = usePagination(response.data.length, response.count);
    const [hiringClientId] = useState(location?.state?.hiringClientId);
    const requestID = location?.state?.requestID;

    const filterConfig = [
        {
            name: 'priorityFilter',
            value: filter.priority,
            selectedId: selectedId.priorityId,
            handleFunction: (value, id) => {
                setFilter({ ...filter, priority: value });
                setSelectedId({ ...selectedId, priorityId: id });
            },
            options: documentPriorityOptions,
        },
        {
            name: 'statusFilter',
            value: filter.status,
            selectedId: selectedId.statusId,
            handleFunction: (value, id) => {
                setFilter({ ...filter, status: value });
                setSelectedId({ ...selectedId, statusId: id });
            },
            options: documentStatusOptions,
        },
    ];

    useEffect(() => {
        if (searchParams.has('status_id') || searchParams.has('priority_id')) {
            let newFilter = Object.assign({}, filter);
            let newSelectedId = Object.assign({}, selectedId);
            if (searchParams.has('status_id')) {
                const statusId = searchParams.get('status_id');
                const value = documentStatusOptions.find((option) => option.id === statusId)?.value;
                newFilter.status = value;
                newSelectedId.statusId = statusId;
            }
            if (searchParams.has('priority_id')) {
                const priorityId = searchParams.get('priority_id');
                const value = documentPriorityOptions.find(
                    (option) => option.id === priorityId,
                )?.value;
                newFilter.priority = value;
                newSelectedId.priorityId = priorityId;
            }
            setFilter(newFilter);
            setSelectedId(newSelectedId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams]);

    const [debounceSearch] = useDebouncedValue(search, 500);
    const { data, isFetching, isFetched, isError, error } = useQuery({
        queryKey: [
            QUERY_KEYS.DOCUMENT_REQUEST,
            accountId,
            hiringClientId,
            debounceSearch,
            pagination.offset,
            pagination.limit,
            requestID,
            filter,
        ],
        queryFn: async () =>
            await getAllDocumentRequestByAccount({
                hiring_client_id: hiringClientId,
                account_id: accountId,
                offset: pagination.offset,
                limit: pagination.limit,
                search: debounceSearch,
                filter: requestID ? { id: requestID } : filter,
            }),
        select: (data) => ({ data: data.data, count: data.count }),
        placeholderData: { data: [], count: 0 },
        enabled: !!accountId,
        staleTime: 0,
    });

    useEffect(() => {
        if (data.data && isFetched) {
            setResponse({ data: data.data, count: data.count });
        }
    }, [data, isFetched]);

    if (isError) popup('error', error.message);

    return (
        <>
            <AdminPageHeader
                heading={DOC_CUSTOMER.PAGE_HEADING}
                searchBarPlaceholder={DOC_CUSTOMER.SEARCH_BAR_PLACEHOLDER}
                setSearch={setSearch}
                count={pagination.dataCount}
                setCurrentPage={pagination.setCurrentPage}
                filterConfig={filterConfig}
                backbutton
            />
            <Table
                columns={DOC_CUSTOMER_COLUMNS}
                data={response.data}
                pagination={pagination}
                pageName={DOC_CUSTOMER.PAGE_HEADING}
                selectedTabData={requestID ? response.data[0] : {}}
                stickyColumns={['Documents']}
                isLoading={isFetching}
            />
        </>
    );
};
